import React, { useRef, useState, useEffect, forwardRef } from 'react';
import { View, Text } from '@smarterservices/smarter-ui';
import PropTypes from 'prop-types';
import Hls from 'hls.js';
import { useVideoContainerStyles } from '../../../hooks/useVideoContainerStyles';

const HlsViewer = forwardRef(function HlsViewer(
  {
    width,
    height,
    regionBounds,
    hlsUrl,
    hlsRef,
    streamRef,
    startTime,
    endTime,
    showDebug = false,
    style = {},
    autoplay = true, // Add autoplay prop with default value of true
    sourceVideoWidth = 2560,
    sourceVideoHeight = 1440,
    videoContainerClassName = 'video-container-wrapper-mask',
    onReady,
    onError,
    onPlay,
    onPause,
    onTimeUpdate,
    onEnded,
    onSeeking,
    onSeeked,
    onLoadStart,
    onLoadedMetadata,
    onLoadedData,
    onCanPlay,
    onResize,
    splitPercentage, // restored prop
    ...props
  },
  ref
) {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const parentRef = useRef(null);
  const [hlsInstance, setHlsInstance] = useState(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [viewportSize, setViewportSize] = useState({
    width: width || 300,
    height: height || 600,
  });
  const [videoMetadata, setVideoMetadata] = useState({
    width: sourceVideoWidth,
    height: sourceVideoHeight,
  });
  const [calculatedDimensions, setCalculatedDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [isTimeRangeActive, setIsTimeRangeActive] = useState(false);

  // Use the shared hook for container and video styling
  const { containerStyle, videoStyle, actualSize } = useVideoContainerStyles({
    regionBounds,
    viewportSize,
    videoMetadata,
    isVideoReady,
    videoContainerClassName,
    showDebug,
  });

  // Calculate viewport dimensions based on provided props and region aspect ratio
  useEffect(() => {
    if (!regionBounds || !isVideoReady) return;

    // Get region dimensions
    const regionWidth = regionBounds.width || 0;
    const regionHeight = regionBounds.height || 0;

    if (regionWidth <= 0 || regionHeight <= 0) return;

    // Calculate region aspect ratio
    const regionAspectRatio = regionWidth / regionHeight;

    // Calculate viewport dimensions based on provided props
    let viewportWidth = width;
    let viewportHeight = height;

    // If only width is provided, calculate height based on aspect ratio
    if (width !== undefined && height === undefined) {
      // Handle different width types
      if (typeof width === 'number') {
        // For numeric width, calculate height directly using aspect ratio
        viewportHeight = width / regionAspectRatio;
      } else if (typeof width === 'string' && width.endsWith('%')) {
        // For percentage width, we need to set a fixed height to prevent growing
        const percentValue = parseFloat(width) / 100;
        // Calculate a proportional height based on the aspect ratio
        viewportHeight = `${Math.round((percentValue * 100) / regionAspectRatio)}%`;
      } else {
        // For other string values (like '300px'), extract the numeric part
        const numericWidth = parseFloat(width);
        if (!isNaN(numericWidth)) {
          viewportHeight = numericWidth / regionAspectRatio;
        } else {
          // Fallback to a reasonable default if we can't parse the width
          viewportHeight = 300 / regionAspectRatio;
        }
      }

      setCalculatedDimensions({
        width: viewportWidth,
        height: viewportHeight,
      });
      if (showDebug) {
        console.log('Calculated height from width:', viewportHeight);
      }
    }
    // If only height is provided, calculate width based on aspect ratio
    else if (width === undefined && height !== undefined) {
      const calculatedWidth =
        typeof height === 'number' ? height * regionAspectRatio : null; // Can't calculate for percentage height

      viewportWidth = calculatedWidth;
      setCalculatedDimensions({
        width: viewportWidth,
        height: viewportHeight,
      });
      if (showDebug) {
        console.log('Calculated width from height:', viewportWidth);
      }
    }
    // If both dimensions are provided, use them as is
    else {
      setCalculatedDimensions({
        width: viewportWidth,
        height: viewportHeight,
      });
      if (showDebug) {
        console.log('Using provided dimensions:', {
          width: viewportWidth,
          height: viewportHeight,
        });
      }
    }
  }, [width, height, regionBounds, isVideoReady]);

  // Update viewport size when dimensions change
  useEffect(() => {
    if (!calculatedDimensions.width || !calculatedDimensions.height) return;

    setViewportSize({
      width: calculatedDimensions.width,
      height: calculatedDimensions.height,
    });
  }, [calculatedDimensions]);

  // Initialize video player based on provided source (hlsUrl, hlsRef, or streamRef)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Clean up any existing video source
    if (video.srcObject && !streamRef) {
      video.srcObject = null;
    }

    // Option 1: Using streamRef (direct MediaStream)
    if (streamRef && streamRef.current) {
      try {
        // For streamRef, we don't need to set srcObject as it's already set in the Webrtc component
        // We just need to handle the video element's events and dimensions

        const handleMetadata = () => {
          if (streamRef.current.videoWidth && streamRef.current.videoHeight) {
            setVideoMetadata({
              width: streamRef.current.videoWidth,
              height: streamRef.current.videoHeight,
            });
          }
          setIsVideoReady(true);
        };

        // If the video already has metadata, use it immediately
        if (streamRef.current.readyState >= 1) {
          handleMetadata();
        } else {
          // Otherwise, wait for the metadata to load
          const metadataHandler = () => {
            handleMetadata();
            streamRef.current.removeEventListener(
              'loadedmetadata',
              metadataHandler
            );
          };
          streamRef.current.addEventListener('loadedmetadata', metadataHandler);
        }

        return () => {
          if (streamRef.current) {
            streamRef.current.removeEventListener('loadedmetadata', () => { });
          }
        };
      } catch (error) {
        console.error('Error handling stream reference:', error);
        if (onError) onError(error);
      }
    }
    // Option 2: Using hlsRef (existing HLS video element)
    else if (hlsRef && hlsRef.current) {
      // Use captureStream to get the decoded frames from the reference video
      // Capture at 60fps for smooth high-speed playback
      if (typeof hlsRef.current.captureStream === 'function') {
        const stream = hlsRef.current.captureStream(60);
        video.srcObject = stream;

        // Get video dimensions from the referenced video
        if (hlsRef.current.videoWidth && hlsRef.current.videoHeight) {
          setVideoMetadata({
            width: hlsRef.current.videoWidth,
            height: hlsRef.current.videoHeight,
          });
          if (showDebug) {
            console.log(
              'Video dimensions from referenced video:',
              hlsRef.current.videoWidth,
              hlsRef.current.videoHeight
            );
          }
        }

        // Set video as ready
        setIsVideoReady(true);

        // Expose video element through ref if provided
        if (ref) {
          if (typeof ref === 'function') {
            ref(video);
          } else {
            ref.current = video;
          }
        }

        // Autoplay the video if autoplay is enabled
        if (autoplay) {
          video.play().catch((error) => {
            console.warn('Autoplay failed:', error);
          });
        }
      } else {
        console.error('captureStream not supported in this browser');
      }
    }
    // Option 3: Using hlsUrl (new HLS instance)
    else if (hlsUrl) {
      if (Hls.isSupported()) {
        const hls = new Hls();
        hls.loadSource(hlsUrl);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setIsVideoReady(true);

          // Get video dimensions if available
          if (video.videoWidth && video.videoHeight) {
            setVideoMetadata({
              width: video.videoWidth,
              height: video.videoHeight,
            });
            if (showDebug) {
              console.log(
                'Video dimensions from metadata:',
                video.videoWidth,
                video.videoHeight
              );
            }
          }

          // Expose video element through ref if provided
          if (ref) {
            if (typeof ref === 'function') {
              ref(video);
            } else {
              ref.current = video;
            }
          }

          // Autoplay the video if autoplay is enabled
          if (autoplay) {
            video.play().catch((error) => {
              console.warn('Autoplay failed:', error);
            });
          }
        });

        setHlsInstance(hls);

        return () => {
          hls.destroy();
        };
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // For Safari which has native HLS support
        video.src = hlsUrl;
        video.addEventListener('loadedmetadata', function onMetadataLoaded() {
          if (video.videoWidth && video.videoHeight) {
            setVideoMetadata({
              width: video.videoWidth,
              height: video.videoHeight,
            });
            if (showDebug) {
              console.log(
                'Video dimensions from metadata:',
                video.videoWidth,
                video.videoHeight
              );
            }
          }

          setIsVideoReady(true);

          // Autoplay the video if autoplay is enabled
          if (autoplay) {
            video.play().catch((error) => {
              console.warn('Autoplay failed:', error);
            });
          }

          // Remove the event listener to avoid memory leaks
          video.removeEventListener('loadedmetadata', onMetadataLoaded);
        });

        return () => {
          video.removeEventListener('loadedmetadata', () => { });
        };
      }
    }
  }, [hlsUrl, hlsRef, streamRef, ref, autoplay]);

  // Call onReady callback when video is ready
  useEffect(() => {
    if (isVideoReady && typeof onReady === 'function') {
      onReady({
        videoElement: videoRef.current,
        metadata: videoMetadata,
        viewportSize,
        regionBounds,
      });
    }
  }, [isVideoReady, onReady, videoMetadata, viewportSize, regionBounds]);

  // Handle time range control for HLS video
  useEffect(() => {
    // Only apply time range control if we have a video element and either startTime or endTime is defined
    if (!videoRef.current || (!startTime && !endTime)) {
      setIsTimeRangeActive(false);
      return;
    }

    const video = videoRef.current;

    // Only apply time range control for HLS sources (not for streamRef)
    if (!hlsUrl && !hlsRef) {
      return;
    }

    setIsTimeRangeActive(true);

    // Set initial time if startTime is provided
    if (startTime !== undefined && video.readyState >= 1) {
      video.currentTime = startTime;
      console.log('Set initial time to', startTime);
    }

    // Function to handle timeupdate event
    const handleTimeUpdate = () => {
      // If we reached the end time, pause the video
      if (endTime !== undefined && video.currentTime >= endTime) {
        video.pause();
        // Reset to start time if both start and end are defined
        if (startTime !== undefined) {
          video.currentTime = startTime;
        }
      }
    };

    // Function to handle seeking event
    const handleSeeking = () => {
      // If user tries to seek outside the time range, reset to appropriate boundary
      if (startTime !== undefined && video.currentTime < startTime) {
        video.currentTime = startTime;
      } else if (endTime !== undefined && video.currentTime > endTime) {
        video.currentTime = endTime;
      }
    };

    // Add event listeners
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('seeking', handleSeeking);

    // Clean up event listeners on unmount
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('seeking', handleSeeking);
    };
  }, [videoRef, startTime, endTime, hlsUrl, hlsRef, isVideoReady]);

  // Note: Video positioning and scaling is now handled by the useVideoContainerStyles hook

  // Calculate the root style based on props and calculated dimensions
  const rootStyle = {
    overflow: 'hidden',
    position: 'relative',
    ...(width === undefined && calculatedDimensions.width
      ? { width: calculatedDimensions.width + 'px' }
      : {}),
    ...(height === undefined && calculatedDimensions.height
      ? { height: calculatedDimensions.height + 'px' }
      : {}),
    ...(width === undefined && !calculatedDimensions.width
      ? { width: '100%' }
      : {}),
    ...(height === undefined && !calculatedDimensions.height
      ? { height: '100%' }
      : {}),
    ...(typeof width === 'number' ? { width: width + 'px' } : {}),
    ...(typeof height === 'number' ? { height: height + 'px' } : {}),
    ...(typeof width === 'string' ? { width } : {}),
    ...(typeof height === 'string' ? { height } : {}),
    ...style,
  };

  // Add event listeners for video events
  useEffect(() => {
    // Use the correct video element - hlsRef.current if available, otherwise videoRef.current
    const video = hlsRef?.current || videoRef.current;

    if (!video) {
      return;
    }

    // Helper function to safely call callbacks
    const safeCallback = (callback, event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    };

    // Define event handlers that call the callbacks
    const handleError = (e) => safeCallback(onError, e);
    const handlePlay = (e) => safeCallback(onPlay, e);
    const handlePause = (e) => safeCallback(onPause, e);
    const handleTimeUpdate = (e) => safeCallback(onTimeUpdate, e);
    const handleEnded = (e) => safeCallback(onEnded, e);
    const handleSeeking = (e) => safeCallback(onSeeking, e);
    const handleSeeked = (e) => safeCallback(onSeeked, e);
    const handleLoadStart = (e) => safeCallback(onLoadStart, e);
    const handleLoadedMetadata = (e) => safeCallback(onLoadedMetadata, e);
    const handleLoadedData = (e) => safeCallback(onLoadedData, e);
    const handleCanPlay = (e) => safeCallback(onCanPlay, e);
    const handleResize = (e) => safeCallback(onResize, e);

    // Add event listeners
    video.addEventListener('error', handleError);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('seeking', handleSeeking);
    video.addEventListener('seeked', handleSeeked);
    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('resize', handleResize);

    // If video is already loaded, manually trigger the appropriate event
    if (video.readyState >= 2) { // HAVE_CURRENT_DATA or higher
      handleLoadedData({ type: 'loadeddata', target: video });
    }
    if (video.readyState >= 3) { // HAVE_FUTURE_DATA or higher  
      handleCanPlay({ type: 'canplay', target: video });
    }

    return () => {
      // Remove event listeners
      video.removeEventListener('error', handleError);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('seeking', handleSeeking);
      video.removeEventListener('seeked', handleSeeked);
      video.removeEventListener('loadstart', handleLoadStart);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('resize', handleResize);
    };
  }, [
    onError,
    onPlay,
    onPause,
    onTimeUpdate,
    onEnded,
    onSeeking,
    onSeeked,
    onLoadStart,
    onLoadedMetadata,
    onLoadedData,
    onCanPlay,
    onResize,
    hlsRef, // Add hlsRef to dependencies so effect re-runs when it changes
  ]);

  return (
    <View
      as="div"
      width={viewportSize.width}
      height={viewportSize.height}
      elementRef={(el) => {
        containerRef.current = el;
      }}
      style={rootStyle}
      {...props}
    >
      {/* Container div to properly mask the video on all sides */}
      <div className={videoContainerClassName} style={containerStyle}>
        {streamRef ? (
          // When using streamRef, don't render our own video element
          <div
            style={{
              width: '100%',
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
            }}
          />
        ) : (
          // For hlsUrl or hlsRef, use our own video element
          <video ref={videoRef} style={videoStyle} muted playsInline />
        )}
      </div>

      {showDebug && isVideoReady && (
        <View
          as="div"
          position="absolute"
          bottom="0"
          left="0"
          margin="small"
          borderRadius="medium"
          background="primary-inverse"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
        >
          <Text size="small">
            Viewport:{' '}
            {typeof viewportSize.width === 'number'
              ? viewportSize.width.toFixed(0)
              : viewportSize.width}{' '}
            x{' '}
            {typeof viewportSize.height === 'number'
              ? viewportSize.height.toFixed(0)
              : viewportSize.height}
            <br />
            Region: {regionBounds?.width
              ? regionBounds.width.toFixed(0)
              : 0} x {regionBounds?.height ? regionBounds.height.toFixed(0) : 0}
            <br />
            Actual:{' '}
            {typeof actualSize.width === 'number'
              ? actualSize.width.toFixed(0)
              : 0}{' '}
            x{' '}
            {typeof actualSize.height === 'number'
              ? actualSize.height.toFixed(0)
              : 0}
            <br />
            Aspect:{' '}
            {regionBounds?.width && regionBounds?.height
              ? (regionBounds.width / regionBounds.height).toFixed(3)
              : 0}
            {isTimeRangeActive && (
              <>
                <br />
                Time: {startTime !== undefined ? startTime : 'start'} -{' '}
                {endTime !== undefined ? endTime : 'end'}
              </>
            )}
          </Text>
        </View>
      )}
    </View>
  );

  // Helper function to get the target video element
  const getTargetVideo = React.useCallback(() => {
    if (hlsRef?.current) {
      return hlsRef.current;
    } else if (videoRef.current) {
      return videoRef.current;
    }
    return null;
  }, [hlsRef]);

  // Expose seek function via ref
  React.useImperativeHandle(ref, () => ({
    seek: (timeInSeconds) => {
      const targetVideo = getTargetVideo();
      console.log('HlsViewer seek - targetVideo:', targetVideo);
      console.log('HlsViewer seek - timeInSeconds:', timeInSeconds);

      if (targetVideo && !isNaN(timeInSeconds) && timeInSeconds >= 0) {
        const duration = targetVideo.duration || Infinity;
        const clampedTime = Math.max(0, Math.min(timeInSeconds, duration));

        console.log('HlsViewer seek - duration:', duration);
        console.log('HlsViewer seek - clampedTime:', clampedTime);

        try {
          targetVideo.currentTime = clampedTime;
          console.log(`HlsViewer: Successfully seeked to ${clampedTime} seconds`);
          return clampedTime;
        } catch (error) {
          console.error('HlsViewer seek error:', error);
          return null;
        }
      } else {
        console.warn('HlsViewer: Cannot seek - video not ready or invalid time', {
          hasVideo: !!targetVideo,
          timeInSeconds,
          isValidTime: !isNaN(timeInSeconds) && timeInSeconds >= 0
        });
        return null;
      }
    },
    getCurrentTime: () => {
      const targetVideo = getTargetVideo();
      return targetVideo ? targetVideo.currentTime : 0;
    },
    getDuration: () => {
      const targetVideo = getTargetVideo();
      return targetVideo ? targetVideo.duration : 0;
    },
    play: () => {
      const targetVideo = getTargetVideo();
      if (targetVideo) {
        return targetVideo.play();
      }
    },
    pause: () => {
      const targetVideo = getTargetVideo();
      if (targetVideo) {
        targetVideo.pause();
      }
    },
    isPaused: () => {
      const targetVideo = getTargetVideo();
      return targetVideo ? targetVideo.paused : true;
    }
  }), [getTargetVideo]);

});

HlsViewer.propTypes = {
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  regionBounds: PropTypes.shape({
    id: PropTypes.string,
    top: PropTypes.number.isRequired,
    left: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    changeDate: PropTypes.string,
    sessionChangeTime: PropTypes.number,
    aspectRatio: PropTypes.number,
  }).isRequired,
  hlsUrl: PropTypes.string,
  hlsRef: PropTypes.object,
  streamRef: PropTypes.object,
  startTime: PropTypes.number,
  endTime: PropTypes.number,
  showDebug: PropTypes.bool,
  autoplay: PropTypes.bool,
  style: PropTypes.object,
  onReady: PropTypes.func,
  onError: PropTypes.func,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
  onTimeUpdate: PropTypes.func,
  onEnded: PropTypes.func,
  onSeeking: PropTypes.func,
  onSeeked: PropTypes.func,
  onLoadStart: PropTypes.func,
  onLoadedMetadata: PropTypes.func,
  onLoadedData: PropTypes.func,
  onCanPlay: PropTypes.func,
  onResize: PropTypes.func,
  sourceVideoWidth: PropTypes.number,
  sourceVideoHeight: PropTypes.number,
  videoContainerClassName: PropTypes.string,
};

// Custom validation to ensure either hlsUrl or hlsRef is provided
HlsViewer.propTypes.isRequired = function (props, propName, componentName) {
  if (!props.hlsUrl && !props.hlsRef && !props.streamRef) {
    return new Error(
      `One of 'hlsUrl', 'hlsRef', or 'streamRef' is required in '${componentName}'.`
    );
  }
};

export default HlsViewer;
